/**
 * PlanStatusBanner — Persistent banner for plan lifecycle warnings (desktop).
 *
 * ACTIVE       → renders nothing
 * GRACE_PERIOD → amber: "Plan expires in {N} days"
 * FROZEN       → red:   "Account frozen — read-only mode."
 */

import type { JSX } from 'react';
import { AlertCircle, Snowflake } from 'lucide-react';
import { useMerchantPlan } from './useMerchantPlan';
import { PLAN_STATUSES } from './features';

export function PlanStatusBanner(): JSX.Element | null {
  const { planStatus, daysUntilExpiry, isFrozen, isGracePeriod, isLoading } = useMerchantPlan();

  if (isLoading || planStatus === PLAN_STATUSES.ACTIVE) return null;

  if (isFrozen) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-danger/10 px-3 py-1.5 text-danger-ink">
        <Snowflake className="h-4 w-4 shrink-0" />
        <span className="text-xs">Account frozen — read-only mode. Upgrade to resume.</span>
      </div>
    );
  }

  if (isGracePeriod) {
    const daysText =
      daysUntilExpiry !== null && daysUntilExpiry > 0
        ? `Plan expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}.`
        : 'Plan has expired. Renew now.';
    return (
      <div className="flex items-center gap-2 rounded-md bg-warning/10 px-3 py-1.5 text-warning-ink">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span className="text-xs">{daysText} Click to renew.</span>
      </div>
    );
  }

  return null;
}
